/**
 * Plans of the hosted offer (docs/16-geschaeftsmodell.md). The limits count
 * what really costs server resources: storage and the CPU jobs (captions,
 * background removal). `null` = unlimited. The backend enforces them for
 * every request (web app and MCP alike), the frontend shows them.
 */
export const PLAN_IDS = ["free", "solo", "team", "agentur", "intern"] as const;
export type PlanId = (typeof PLAN_IDS)[number];

/** Plans that can be booked online. */
export type PaidPlanId = Exclude<PlanId, "intern" | "free">;
export const PAID_PLAN_IDS = ["solo", "team", "agentur"] as const satisfies readonly PaidPlanId[];

export function isPaidPlan(id: string): id is PaidPlanId {
  return (PAID_PLAN_IDS as readonly string[]).includes(id);
}

/** Free trial of a new subscription (card required, charged afterwards unless cancelled). */
export const TRIAL_DAYS = 14;

/** Stripe subscription statuses (https://docs.stripe.com/billing/subscriptions/overview#subscription-statuses). */
export const SUBSCRIPTION_STATUSES = [
  "incomplete",
  "incomplete_expired",
  "trialing",
  "active",
  "past_due",
  "canceled",
  "unpaid",
  "paused",
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

/**
 * Statuses in which an organization can work. past_due: a payment failed and
 * Stripe is still retrying, so the customer gets time to fix the card.
 */
export const WORKING_STATUSES = [
  "trialing",
  "active",
  "past_due",
] as const satisfies readonly SubscriptionStatus[];

export const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionStatus, string> = {
  incomplete: "Zahlung offen",
  incomplete_expired: "Abgebrochen",
  trialing: "Testphase",
  active: "Aktiv",
  past_due: "Zahlung fehlgeschlagen",
  canceled: "Beendet",
  unpaid: "Unbezahlt",
  paused: "Pausiert",
};

/**
 * Version of the terms (AGB) and withdrawal notice customers agree to when they
 * order. Bump it whenever the texts change materially (apps/web/src/routes/legal.tsx).
 */
export const TERMS_VERSION = "2026-09-28";

/** Printed on every invoice: the seller is a small business (Kleinunternehmer). */
export const INVOICE_TAX_NOTE = "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.";

/** Plan of newly created organizations. Existing organizations keep their plan. */
export const DEFAULT_PLAN: PlanId = "free";

export type PlanLimits = {
  /** Members of the organization, pending invitations included. */
  members: number | null;
  documents: number | null;
  uploadBytes: number | null;
  exportPages: number | null;
  exportMaxSide: number | null;
  videoCutouts: number | null;
  /** null = DEFAULT_ORG_QUOTA_GB of the environment. */
  storageBytes: number | null;
  /** Transcribed video per calendar month. */
  captionMinutes: number | null;
  /** Background removals per calendar month. */
  cutouts: number | null;
  brandKits: number | null;
  /** Access through the MCP server (AI assistants). Part of every plan, so not checked yet. */
  mcp: boolean;
};

export type Plan = {
  id: PlanId;
  name: string;
  /** Monthly price in euro cents; null = not for sale. */
  priceCents: number | null;
  /** Shown in the plan comparison. */
  public: boolean;
  limits: PlanLimits;
};

const GB = 1024 ** 3;

const paidExtras = {
  documents: null,
  uploadBytes: null,
  exportPages: null,
  exportMaxSide: null,
  videoCutouts: null,
};

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: "free",
    name: "Free",
    priceCents: 0,
    public: true,
    limits: {
      members: 1,
      storageBytes: 100 * 1024 ** 2,
      captionMinutes: 2,
      cutouts: 3,
      brandKits: 1,
      mcp: true,
      documents: 5,
      uploadBytes: 20 * 1024 ** 2,
      exportPages: 10,
      exportMaxSide: 2048,
      videoCutouts: 0,
    },
  },
  solo: {
    id: "solo",
    name: "Solo",
    priceCents: 700,
    public: true,
    limits: {
      ...paidExtras,
      members: 1,
      storageBytes: 10 * GB,
      captionMinutes: 200,
      cutouts: 100,
      videoCutouts: 100,
      brandKits: 2,
      mcp: true,
    },
  },
  team: {
    id: "team",
    name: "Team",
    priceCents: 1200,
    public: true,
    limits: {
      ...paidExtras,
      members: 5,
      storageBytes: 50 * GB,
      captionMinutes: 500,
      cutouts: 500,
      videoCutouts: 500,
      brandKits: 5,
      mcp: true,
    },
  },
  agentur: {
    id: "agentur",
    name: "Agentur",
    priceCents: 2500,
    public: true,
    limits: {
      ...paidExtras,
      members: 20,
      storageBytes: 200 * GB,
      captionMinutes: 2000,
      cutouts: null,
      brandKits: null,
      mcp: true,
    },
  },
  intern: {
    id: "intern",
    name: "Intern",
    priceCents: null,
    public: false,
    limits: {
      ...paidExtras,
      members: null,
      storageBytes: null,
      captionMinutes: null,
      cutouts: null,
      brandKits: null,
      mcp: true,
    },
  },
};

export const PUBLIC_PLANS: Plan[] = PLAN_IDS.map((id) => PLANS[id]).filter((plan) => plan.public);

export function getPlan(id: string | null | undefined): Plan {
  return PLANS[(id ?? DEFAULT_PLAN) as PlanId] ?? PLANS[DEFAULT_PLAN];
}

/** "7 €", "12 €", "–" for plans that are not for sale. */
export function formatPlanPrice(plan: Plan): string {
  if (plan.priceCents === null) return "–";
  const euros = plan.priceCents / 100;
  return `${euros.toLocaleString("de-DE", { minimumFractionDigits: Number.isInteger(euros) ? 0 : 2 })} €`;
}

/** Whole minutes for display; started minutes count (like the check does in seconds). */
export const captionMinutesUsed = (seconds: number) => Math.ceil(seconds / 60);

/**
 * The next public plan that raises the given limit, for "Mehr gibt es im Tarif …"
 * hints. null: no public plan offers more.
 */
export function upgradeFor(current: PlanId, limit: Exclude<keyof PlanLimits, "mcp">): Plan | null {
  const own = PLANS[current].limits[limit];
  if (own === null) return null;
  return (
    PUBLIC_PLANS.find((plan) => {
      const value = plan.limits[limit];
      return value === null || value > own;
    }) ?? null
  );
}
